import React, { useMemo, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { getProductPrice, calcProfit } from '../utils/pricing';
import ExportCSV from '../components/reports/ExportCSV';

export default function ReportsPage() {
    const { products, sales, expenses } = useAppContext();
    const [month, setMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });

    const { filteredSales, filteredExpenses, summaryByProduct, monthlySummary } =
        useMemo(() => {
            const [year, m] = month.split('-');
            const monthInt = Number(m);

            const fs = sales.filter((s) => {
                const d = new Date(s.date);
                return d.getFullYear() === Number(year) && d.getMonth() + 1 === monthInt;
            });

            const fe = expenses.filter((e) => {
                const d = new Date(e.date);
                return d.getFullYear() === Number(year) && d.getMonth() + 1 === monthInt;
            });

            const byProduct = new Map();
            fs.forEach((s) => {
                const product = products.find((p) => p.id === s.productId);
                if (!product) return;
                const price = getProductPrice(product);
                const revenue = price * s.quantity;
                const cost = product.unitCost * s.quantity;
                const entry = byProduct.get(product.id) || {
                    name: product.name,
                    qty: 0,
                    revenue: 0,
                    cost: 0,
                };
                entry.qty += s.quantity;
                entry.revenue += revenue;
                entry.cost += cost;
                byProduct.set(product.id, entry);
            });

            const summaryByProductArr = Array.from(byProduct.values()).map((p) => ({
                ...p,
                profit: p.revenue - p.cost,
                marginPercent: p.revenue > 0 ? ((p.revenue - p.cost) / p.revenue) * 100 : 0,
            }));

            const { revenue, totalExpenses, profit, marginPercent } = calcProfit(
                products,
                fs,
                fe
            );

            return {
                filteredSales: fs,
                filteredExpenses: fe,
                summaryByProduct: summaryByProductArr,
                monthlySummary: { revenue, totalExpenses, profit, marginPercent },
            };
        }, [month, products, sales, expenses]);

    const format = (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2 });

    return (
        <div>
            <h1 className="h3 mb-4">Relatórios</h1>

            <div className="row mb-4">
                <div className="col-md-4">
                    <label className="form-label">Mês de referência</label>
                    <input
                        type="month"
                        className="form-control"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                    />
                </div>
            </div>

            <div className="row g-4 mb-4">
                <div className="col-md-3">
                    <div className="card text-bg-primary">
                        <div className="card-body">
                            <div className="small">Receita</div>
                            <div className="fs-5 fw-semibold">
                                R$ {format(monthlySummary.revenue || 0)}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card text-bg-danger">
                        <div className="card-body">
                            <div className="small">Despesas</div>
                            <div className="fs-5 fw-semibold">
                                R$ {format(monthlySummary.totalExpenses || 0)}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card text-bg-success">
                        <div className="card-body">
                            <div className="small">Lucro</div>
                            <div className="fs-5 fw-semibold">
                                R$ {format(monthlySummary.profit || 0)}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card text-bg-info">
                        <div className="card-body">
                            <div className="small">Margem</div>
                            <div className="fs-5 fw-semibold">
                                {(monthlySummary.marginPercent || 0).toFixed(1)} %
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Rentabilidade por produto */}
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2 className="h6 mb-0">Rentabilidade por produto</h2>
                        <ExportCSV
                            data={summaryByProduct}
                            fileName={`rentabilidade_${month}`}
                            headers={['Produto', 'Qtd', 'Receita', 'Custo', 'Lucro', 'Margem %']}
                            mapRow={(p) => [
                                p.name,
                                p.qty,
                                p.revenue.toFixed(2),
                                p.cost.toFixed(2),
                                p.profit.toFixed(2),
                                p.marginPercent.toFixed(1),
                            ]}
                        />
                    </div>
                    {/* tabela semelhante às outras, com dados de summaryByProduct */}
                </div>
            </div>
        </div>
    );
}
